import { useEffect, useState } from "react";
import { api, formatApiError, money, fmtDateTime } from "../lib/api";
import { toast } from "sonner";
import { LockOpen, Warning, CheckCircle } from "@phosphor-icons/react";

/**
 * Reusable Open-Shift dialog.
 *
 * Props:
 *   onClose()           — closes without opening (modal can be skipped)
 *   onOpened(shift)     — fires after successful POST /shifts/open
 *   forced              — when true, hides the "Cancel" button and shows a
 *                         "Sales are blocked until the shift is open" notice.
 *                         Used by the post-login auto-popup.
 *   storeName           — optional, displayed in the header for context when
 *                         the dialog is fired globally on login.
 */
export default function OpenShiftDialog({ onClose, onOpened, forced = false, storeName = "" }) {
  const [opening, setOpening] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const [reason, setReason] = useState("");

  // Staff count the drawer themselves (blank field) so a missing float is
  // caught instead of silently re-typed from the last close.
  useEffect(() => {
    api.get("/shifts/last-closed").then((r) => setLast(r.data?.shift || null)).catch(() => setLast(null));
  }, []);

  const expected = last ? Number(last.counted_cash || 0) : null;
  const diff = expected != null && opening !== "" ? Math.round((Number(opening) - expected) * 100) / 100 : 0;
  const mismatch = Math.abs(diff) >= 0.005;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/shifts/open", {
        opening_float: Number(opening || 0),
        notes,
        opening_variance_reason: mismatch ? reason.trim() : "",
      });
      if (mismatch) toast.warning(`Shift opened ${diff < 0 ? "short" : "over"} $${money(Math.abs(diff))} — admin has been alerted`);
      else toast.success(`Shift opened · float $${money(r.data.opening_float)}`);
      onOpened?.(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      data-testid="open-shift-dialog"
      onClick={forced ? undefined : onClose}
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl overflow-hidden"
        style={{ boxShadow: "0 32px 80px rgba(0,0,0,0.18)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-[#F3F4F6] flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#DBEAFE] text-[#1E5FEB] flex items-center justify-center shrink-0">
            <LockOpen size={18} weight="bold" />
          </div>
          <div>
            <div className="font-display font-semibold text-[18px]">
              Open shift{storeName ? ` · ${storeName}` : ""}
            </div>
            <div className="text-[12px] text-[#9CA3AF] mt-0.5">
              {forced
                ? "Sales are blocked until the till is open. Count the cash currently in the drawer to start."
                : "Count the cash currently in the drawer to start."}
            </div>
          </div>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {last && (
            <div className="rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] px-4 py-3 flex items-center justify-between" data-testid="open-shift-last-close">
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-[#6B7280] font-semibold">Left in drawer at last close</div>
                <div className="text-[11px] text-[#9CA3AF] mt-0.5">{last.closed_by_name || "—"} · {fmtDateTime(last.closed_at)}</div>
              </div>
              <div className="font-mono font-bold text-[18px] text-[#121216]" data-testid="open-shift-expected">${money(expected)}</div>
            </div>
          )}
          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] uppercase tracking-[0.12em] text-[#4B5563] font-medium">Cash counted in drawer now ($)</span>
            <input
              data-testid="open-shift-float"
              type="number"
              step="0.01"
              min="0"
              inputMode="decimal"
              placeholder="Count the drawer"
              value={opening}
              onChange={(e) => setOpening(e.target.value)}
              autoFocus
              className={`h-11 w-full rounded-xl border px-3 font-mono text-[15px] focus:outline-none ${mismatch ? "border-[#DC2626] focus:border-[#DC2626]" : "border-[#E5E7EB] focus:border-[#1E5FEB]"}`}
              required
            />
          </label>
          {expected != null && opening !== "" && (mismatch ? (
            <div className="rounded-xl border-2 border-[#FCA5A5] bg-[#FEF2F2] px-4 py-3 space-y-2.5" data-testid="open-shift-mismatch">
              <div className="flex items-center gap-2 text-[#B91C1C]">
                <Warning size={18} weight="fill" />
                <span className="text-[14px] font-bold" data-testid="open-shift-variance">
                  Opening {diff < 0 ? "short" : "over"} {diff < 0 ? "−" : "+"}${money(Math.abs(diff))}
                </span>
              </div>
              <div className="text-[12px] text-[#7F1D1D]">
                Doesn't match the ${money(expected)} left at last close. The admin will be alerted and this shows on the till history and Z-report.
              </div>
              <textarea
                data-testid="open-shift-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                placeholder="Reason (required) — e.g. Owner banked $300 on Saturday"
                className="w-full rounded-lg border border-[#FCA5A5] bg-white px-3 py-2 text-[13px] focus:border-[#DC2626] focus:outline-none"
              />
            </div>
          ) : (
            <div className="flex items-center gap-2 text-[12px] font-semibold text-[#0F9D58]" data-testid="open-shift-match">
              <CheckCircle size={16} weight="fill" /> Matches last close
            </div>
          ))}
          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] uppercase tracking-[0.12em] text-[#4B5563] font-medium">Notes (optional)</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="e.g. Morning shift · Sarah"
              className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 text-[14px] focus:border-[#1E5FEB] focus:outline-none"
            />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            {!forced && (
              <button
                type="button"
                onClick={onClose}
                className="h-11 px-4 rounded-xl border border-[#E5E7EB] text-[14px] font-medium hover:bg-[#F3F4F6]"
              >
                Cancel
              </button>
            )}
            {forced && (
              <button
                type="button"
                onClick={onClose}
                data-testid="open-shift-skip"
                className="h-11 px-4 rounded-xl border border-[#E5E7EB] text-[12px] font-medium text-[#9CA3AF] hover:text-[#121216]"
                title="Continue without opening — sales will be blocked"
              >
                Skip for now
              </button>
            )}
            <button
              type="submit"
              disabled={busy || opening === "" || (mismatch && reason.trim().length < 3)}
              data-testid="open-shift-submit"
              className="h-11 px-5 rounded-xl bg-[#1E5FEB] text-white text-[14px] font-semibold hover:bg-[#1849C8] disabled:opacity-60"
            >
              {busy ? "Opening…" : "Open shift"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
